import { hc } from 'hono/client';
import type { AppType, GenerationDto, MediaDto } from '@str/api/types';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    /** Structured facts from the API (a credit shortfall's needed, balance and actions). */
    public details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

/** Typed Hono RPC client; same-origin (Vite proxies /api in dev, the Worker serves both in prod). */
export const api = hc<AppType>('/', { init: { credentials: 'include' } });

type ErrorBody = {
  error?: { code?: string; message?: string; details?: Record<string, unknown> };
};

type ResponseLike = { ok: boolean; status: number; json(): Promise<unknown> };

/** Unwraps a Hono RPC response, throwing ApiError for non-2xx responses. */
export async function unwrap<T>(promise: Promise<ResponseLike>): Promise<T> {
  const res = await promise;
  if (!res.ok) {
    let body: ErrorBody = {};
    try {
      body = (await res.json()) as ErrorBody;
    } catch {
      /* non-JSON error */
    }
    throw new ApiError(
      res.status,
      body.error?.code ?? 'error',
      body.error?.message ?? `Request failed (${res.status})`,
      body.error?.details,
    );
  }
  return (await res.json()) as T;
}

/** Stops the running Reel render for a post; the API answers 404 when none is running. */
export function cancelRender(postId: string) {
  return unwrap<{ generation: GenerationDto }>(
    api.api.posts[':id'].render.cancel.$post({ param: { id: postId } }),
  ).then((r) => r.generation);
}

/** Uploads one file; `onboarding` routes it through the pre-signup session endpoint. */
export async function uploadFile(
  propertyId: string,
  file: File,
  opts: { onboarding?: boolean } = {},
) {
  if (file.type.startsWith('audio/') && file.size > 25 * 1024 * 1024)
    throw new ApiError(
      400,
      'bad_request',
      'Audio must be 25 MB or smaller. Choose a shorter or compressed file.',
    );
  const base = opts.onboarding ? '/api/onboarding/properties' : '/api/properties';
  if (file.type.startsWith('video/')) return uploadVideo(`${base}/${propertyId}/media`, file);
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${base}/${propertyId}/media`, {
    method: 'POST',
    body: form,
    credentials: 'include',
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ErrorBody;
    throw new ApiError(
      res.status,
      body.error?.code ?? 'error',
      body.error?.message ?? 'Upload failed',
    );
  }
  const data: unknown = await res.json();
  return data as { media: MediaDto };
}

type UploadStart = { upload: { token: string; chunkSize: number; partCount: number } };
type UploadedPart = { part: { partNumber: number; etag: string; proof: string } };

async function checkedJson<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ErrorBody;
    throw new ApiError(res.status, body.error?.code ?? 'error', body.error?.message ?? fallback);
  }
  return res.json<T>();
}

async function uploadVideo(base: string, file: File) {
  const started = await checkedJson<UploadStart>(
    await fetch(`${base}/uploads`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ name: file.name, type: file.type, size: file.size }),
      credentials: 'include',
    }),
    'Upload could not start',
  );
  const { token, chunkSize, partCount } = started.upload;
  const parts: UploadedPart['part'][] = [];
  try {
    for (let partNumber = 1; partNumber <= partCount; partNumber++) {
      const start = (partNumber - 1) * chunkSize;
      const body = file.slice(start, Math.min(file.size, start + chunkSize));
      const uploaded = await checkedJson<UploadedPart>(
        await fetch(`${base}/uploads/${partNumber}?token=${encodeURIComponent(token)}`, {
          method: 'PUT',
          body,
          credentials: 'include',
        }),
        'Video upload failed',
      );
      parts.push(uploaded.part);
    }
    return await checkedJson<{ media: MediaDto }>(
      await fetch(`${base}/uploads/complete`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token, parts }),
        credentials: 'include',
      }),
      'Video upload could not finish',
    );
  } catch (error) {
    await fetch(`${base}/uploads`, {
      method: 'DELETE',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token }),
      credentials: 'include',
    }).catch(() => undefined);
    throw error;
  }
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return 'Something went wrong';
}

export type CreditShortfall = {
  needed: number;
  balance: number;
  actions: ('buy_pack' | 'start_plan')[];
};

/** The shortfall an error carries when the wallet could not cover a run, else null. */
export function creditShortfall(e: unknown): CreditShortfall | null {
  if (!(e instanceof ApiError) || e.code !== 'credits_required' || !e.details) return null;
  const d = e.details as Partial<CreditShortfall>;
  if (typeof d.needed !== 'number' || typeof d.balance !== 'number') return null;
  return { needed: d.needed, balance: d.balance, actions: d.actions ?? ['buy_pack'] };
}
